import React, { useState, useEffect } from 'react';
import { predictWithModel, predictBatchWithModel, getModelsRegistry } from '../api';
import { EmptyState, ErrorState } from './shared/UIComponents';
import { useActiveModel } from '../context/ActiveModelContext';

const inferTaskTypeFromModelType = (modelType) => {
  if (!modelType) return 'unknown';
  const normalized = String(modelType).toLowerCase();
  if (normalized.includes('linear') || normalized.includes('regression')) return 'regression';
  if (normalized.includes('logistic') || normalized.includes('svm') || normalized.includes('forest') || normalized.includes('neural')) return 'classification';
  return 'unknown';
};

const GUIDE_STORAGE_KEY = 'biodawn.prediction.guideOpen';

/**
 * Shape a single prediction like a row parsed from a batch results CSV, so the
 * prediction results table can display it alongside batch output.
 */
const buildSingleResultRow = (result, featureData, sampleLabel) => {
  const rawPrediction = Number(result.prediction) || 0;
  const probPos = result.probability_positive_class ?? null;
  const probNeg = result.probability_negative_class ?? null;
  const hasProbabilities = probPos != null && probNeg != null;
  const isBinaryPrediction = rawPrediction === 0 || rawPrediction === 1;

  let prediction;
  let confidenceScore;
  if (hasProbabilities) {
    prediction = probPos > probNeg ? 'True' : 'False';
    confidenceScore = Math.max(probPos, probNeg);
  } else if (isBinaryPrediction) {
    prediction = rawPrediction === 1 ? 'True' : 'False';
    confidenceScore = 0.8;
  } else {
    prediction = rawPrediction.toString();
    confidenceScore = null;
  }

  return {
    compound_id: sampleLabel?.trim() || 'Manual entry',
    compound_id_header: 'Gene',
    prediction,
    confidence_score: confidenceScore,
    raw_prediction: rawPrediction,
    probability_negative_class: probNeg,
    probability_positive_class: probPos,
    feature_values: { ...featureData },
    is_single_prediction: true
  };
};

// True/False only makes sense for a binary run; anything wider is labelled by class.
const classLabelFor = (rawClass, isBinary) => {
  if (!isBinary) return `Predicted class ${rawClass}`;
  return rawClass === '1' ? 'Predicted True' : 'Predicted False';
};


// Turn a batch run's raw stats into per-class counts

const getClassBreakdown = (summary, totalPredictions, taskType) => {
  if (taskType !== 'classification' || !summary || !totalPredictions) return null;

  const toEntry = (rawClass, count, isBinary) => ({
    label: classLabelFor(rawClass, isBinary),
    count,
    percent: (count / totalPredictions) * 100
  });

  const distribution = summary.class_distribution;
  if (distribution && Object.keys(distribution).length > 0) {
    const classes = Object.keys(distribution);
    const isBinary = classes.length === 2 && classes.every((c) => c === '0' || c === '1');
    // Binary reads best positives-first; wider runs stay in class order.
    const ordered = isBinary
      ? ['1', '0']
      : classes.sort((a, b) => Number(a) - Number(b));
    return ordered.map((rawClass) => toEntry(rawClass, distribution[rawClass] ?? 0, isBinary));
  }

  const positiveRate = summary.mean_prediction;
  if (positiveRate == null || Number.isNaN(positiveRate)) return null;
  const positives = Math.round(positiveRate * totalPredictions);
  return [
    toEntry('1', positives, true),
    toEntry('0', totalPredictions - positives, true)
  ];
};

const ModelPrediction = ({ datasets, selectedDataset, modelTypes, selectedModel }) => {
  const [selectedModelId, setSelectedModelId] = useState('');
  const [guideOpen, setGuideOpen] = useState(() => {
    try {
      return window.localStorage.getItem(GUIDE_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const { activeModel } = useActiveModel();
  const [availableModels, setAvailableModels] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [batchResultInfo, setBatchResultInfo] = useState(null);
  const [singleFeatures, setSingleFeatures] = useState({});
  // Label only - never sent to the model, just carried onto the result row
  const [sampleLabel, setSampleLabel] = useState('');
  const MAX_MANUAL_FEATURES = 25; // Limit for manual feature input to prevent UI overload

  // Load available models when component mounts
  useEffect(() => {
    loadAvailableModels();
  }, []);

  const loadAvailableModels = async () => {
    try {
      const response = await getModelsRegistry();
      if (response && response.models) {
        const models = response.models.map(model => ({
          id: model.id,
          name: `${model.id} - ${model.model_type} - ${model.dataset_name}`,
          dataset: model.dataset_name,
          type: model.model_type,
          taskType: model.metadata?.task_type || model.task_type || inferTaskTypeFromModelType(model.model_type),
          decisionThreshold: model.decision_threshold ?? model.metadata?.decision_threshold ?? null,
          accuracy: model.metrics?.accuracy,
          created_at: model.created_at,
          featureNames: model.metadata?.feature_names || model.feature_names || []
        }));
        setAvailableModels(models);
      }
    } catch (err) {
      console.error('Failed to load models:', err);
      // Fallback to placeholder models if API fails
      const models = [
        { id: 'model_1', name: 'Random Forest - Dataset 1', dataset: 'dataset1', type: 'random_forest' },
        { id: 'model_2', name: 'SVM - Dataset 2', dataset: 'dataset2', type: 'svm' },
        { id: 'model_3', name: 'Neural Network - Dataset 3', dataset: 'dataset3', type: 'neural_network' }
      ];
      setAvailableModels(models);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // Validate file type: accept only CSV or Excel files
    const isSupported = file && /\.(csv|xlsx|xls)$/i.test(file.name);
    if (isSupported) {
      setUploadedFile(file);
      setError(null);
    } else {
      setError('Please upload a CSV or Excel (.xlsx, .xls) file');
      setUploadedFile(null);
    }
  };

  // Handle model selection change - default to active model context
  // If selectedModel prop changes, update selectedModelId accordingly
  useEffect(() => {
    if (activeModel) {
      setSelectedModelId(activeModel.id);
    }
  }, [activeModel]);
  const handleModelSelection = (event) => {
    setSelectedModelId(event.target.value);
    setError(null);
  };

  // Dynamically rebuild the input state dictionary when the selected model changes
  useEffect(() => {
    if (selectedModelId) {
      const model = availableModels.find(m => m.id === selectedModelId);
      if (model && model.featureNames && model.featureNames.length > 0) {
        const initialFeatures = {};
        model.featureNames.forEach(f => { initialFeatures[f] = ''; });
        setSingleFeatures(initialFeatures);
      } else {
        setSingleFeatures({});
      }
    } else {
      setSingleFeatures({});
    }
  }, [selectedModelId, availableModels]);

  const selectedModelDetails = availableModels.find((model) => model.id === selectedModelId) || null;
  const featuresToRender = selectedModelDetails?.featureNames?.length > 0 ? selectedModelDetails.featureNames : Object.keys(singleFeatures);


  const handleSinglePrediction = async () => {
    if (!selectedModelId) {
      setError('Please select a model');
      return;
    }

    // Validate that no required feature was left blank
    const missingFeature = featuresToRender.find(f => singleFeatures[f] === '' || singleFeatures[f] === undefined);
    if (missingFeature) {
      setError(`Please fill in all feature values. Missing: ${missingFeature}`);
      return;
    }

    // Convert string inputs to floats using the exact feature names
    const featureData = {};
    featuresToRender.forEach(f => {
      featureData[f] = parseFloat(singleFeatures[f]);
    });

    try {
      setLoading(true);
      setError(null);
      
      const result = await predictWithModel(selectedModelId, featureData);
      
      // Check if a prediction value or error key exists natively in the payload
      if (result && result.prediction !== undefined && !result.error) {
        // Single predictions are not saved server-side, so hand the row straight to the
        // results table below rather than rendering a separate result panel here.
        window.dispatchEvent(new CustomEvent('singlePredictionCompleted', {
          detail: { row: buildSingleResultRow(result, featureData, sampleLabel) }
        }));
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || result.message || 'Prediction failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during prediction');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchPrediction = async () => {
    if (!selectedModelId) {
      setError('Please select a model');
      return;
    }

    if (!uploadedFile) {
      setError('Please upload a CSV or Excel file for batch prediction');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use the proper API function for batch prediction
      const result = await predictBatchWithModel(selectedModelId, uploadedFile);
      
      console.log('Batch prediction result:', result); // Debug log
      
      if (result.sample_predictions) {
        // No per-sample cards here: every row lands in the prediction results table
        // below, so cards would just duplicate it. Only the run summary is shown.
        setBatchResultInfo({
          totalPredictions: result.total_predictions,
          resultsFilename: result.results_filename,
          modelType: result.model_type,
          datasetName: result.dataset_name,
          predictionSummary: result.prediction_summary
        });
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        
        // Show additional info about the batch prediction
        console.log(`Batch prediction completed: ${result.total_predictions} total predictions`);
        console.log(`Results saved to: ${result.results_filename}`);
        
        // Trigger a custom event to notify other components to refresh
        window.dispatchEvent(new CustomEvent('batchPredictionCompleted', {
          detail: { resultsFilename: result.results_filename }
        }));
      } else {
        setError('No predictions returned from batch prediction');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during batch prediction');
    } finally {
      setLoading(false);
    }
  }

  const toggleGuide = () => {
    setGuideOpen(prev => {
      const next = !prev;
      try {
        window.localStorage.setItem(GUIDE_STORAGE_KEY, String(next));
      } catch {
      }
      return next;
    });
  };

  const clearResults = () => {
    setBatchResultInfo(null);
    setError(null);
    setSuccess(false);
  };

  const selectedTaskType = (activeModel?.id === selectedModelId
    ? (activeModel?.metadata?.task_type || activeModel?.task_type)
    : selectedModelDetails?.taskType) || inferTaskTypeFromModelType(selectedModelDetails?.type);
  const batchClassBreakdown = getClassBreakdown(
    batchResultInfo?.predictionSummary,
    batchResultInfo?.totalPredictions,
    selectedTaskType
  );

  return (
    <div className="prediction-container">
      <header className="prediction-container__header">
        <h2 className="prediction-container__title">
          Model Prediction
        </h2>
        <p className="prediction-container__subtitle">
          Run one of your trained models on new, unlabeled data to get predicted outcomes.
          Score a single sample by typing its feature values, or score a whole file at once by
          uploading a CSV or Excel file.
        </p>
      </header>

      {/* How-to guide */}
      <section className="prediction-guide">
        <button
          type="button"
          className="prediction-guide__toggle"
          onClick={toggleGuide}
          aria-expanded={guideOpen}
          aria-controls="prediction-guide-body"
        >
          <span className="prediction-guide__toggle-label">How to use this tab</span>
          <span className="prediction-guide__chevron" aria-hidden="true">{guideOpen ? '▾' : '▸'}</span>
        </button>

        {guideOpen && (
          <div className="prediction-guide__body" id="prediction-guide-body">
            <ol className="prediction-guide__steps">
              <li>
                <strong>Pick a model.</strong> The dropdown lists every model you have already
                trained, with its type, source dataset and accuracy. Nothing is trained here — if
                the list is empty, train a model first.
              </li>
              <li>
                <strong>Provide the input data.</strong> Fill in the feature boxes for a single
                sample, or upload a CSV/Excel file to score many samples at once. The feature boxes appear
                automatically once a model is selected, and match the exact features that model was
                trained on.
              </li>
              <li>
                <strong>Run the prediction.</strong> Use <em>Single Prediction</em> for the typed
                values, or <em>Batch Prediction</em> for the uploaded file.
              </li>
              <li>
                <strong>Read the results.</strong> Everything lands in the prediction results
                table below. A batch run fills the table from its results file and reports a
                summary here; a single prediction is pinned to the top of the table as a
                highlighted row, named after the gene/sample name you entered.
              </li>
            </ol>

            <div className="prediction-guide__modes">
              <div className="prediction-guide__mode">
                <h5 className="prediction-guide__mode-title">Single prediction</h5>
                <p>
                  Best for a quick what-if on one sample. Every feature box must be filled in.
                  Models with more than {MAX_MANUAL_FEATURES} features are batch-only, since typing
                  that many values by hand is impractical.
                </p>
              </div>
              <div className="prediction-guide__mode">
                <h5 className="prediction-guide__mode-title">Batch prediction</h5>
                <p>
                  Best for scoring a full dataset. Your CSV or Excel file needs one row per sample and one column
                  per feature, using the same column names as the training data, and{' '}
                  <strong>no target/label column</strong>. Results are saved to a file and loaded
                  into the prediction results table below.
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="prediction-container__content">
        {availableModels.length === 0 && (
          <div style={{ marginBottom: '16px', width: '100%' }}>
            <EmptyState
              title="No models available"
              description="Train and validate a model first, then return here to run predictions with confidence guidance."
              icon="🧠"
              actionLabel="Refresh models"
              actionAriaLabel="Refresh available models"
              onAction={loadAvailableModels}
              autoFocusAction={true}
            />
          </div>
        )}
        {/* Model Selection */}
        <div className="model-selection">
          <div className="model-selection__field">
            <div className="model-selection__header">
              <label className="model-selection__label">
                Select Model
              </label>
              <button
                onClick={loadAvailableModels}
                className="model-selection__refresh-btn"
                aria-label="Refresh available models"
              >
                ↻ Refresh
              </button>
            </div>
            <select
              value={selectedModelId}
              onChange={handleModelSelection}
              className="model-selection__select"
              aria-label="Select trained model for prediction"
            >
              <option value="">Choose a model...</option>
              {availableModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.name} {model.accuracy ? `(Acc: ${(model.accuracy * 100).toFixed(1)}%)` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Single Prediction Feature Inputs */}
          <div className="feature-inputs">
            <label className="feature-inputs__label" htmlFor="single-sample-label">
              Gene / sample name (optional)
            </label>
            <input
              id="single-sample-label"
              type="text"
              value={sampleLabel}
              onChange={(e) => setSampleLabel(e.target.value)}
              className="feature-inputs__input"
              placeholder="e.g. APOE"
            />
            <p className="feature-inputs__hint">
              Labels the result row only — it is not used to make the prediction.
            </p>

            <label className="feature-inputs__label">
              Feature Values (for single prediction)
            </label>

            {!selectedModelId ? (
              <p style={{ fontSize: '0.9rem', color: '#666' }}>Please select a model to view required features.</p>
            ) : featuresToRender.length > MAX_MANUAL_FEATURES ? (
              <div className="alert alert--warning" style={{ fontSize: '0.9rem', marginTop: '8px' }}>
                This model requires <strong>{featuresToRender.length}</strong> features. Manual entry is disabled to prevent browser freezing. Please upload a CSV or Excel file and use Batch Prediction instead.
              </div>
            ) : (
              <div className="feature-inputs__grid" style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                {featuresToRender.map((featureName) => (
                  <div key={featureName} className="feature-inputs__field">
                    <label className="feature-inputs__field-label" title={featureName}>
                      {featureName.length > 25 ? featureName.substring(0, 25) + '...' : featureName}
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={singleFeatures[featureName] ?? ''}
                      onChange={(e) => setSingleFeatures(prev => ({ ...prev, [featureName]: e.target.value }))}
                      className="feature-inputs__input"
                      placeholder="0.0"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="file-upload">
            <label className="file-upload__label">
              Upload Feature File (Optional for batch prediction)
            </label>
            <p className="file-upload__hint">
              CSV or Excel (.xlsx, .xls). One row per sample, one column per feature, no target column.
            </p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="file-upload__input"
            />
            {uploadedFile && (
              <p className="file-upload__status file-upload__status--success">
                ✓ {uploadedFile.name} uploaded
              </p>
            )}
          </div>
        </div>

        {/* Prediction Actions */}
        <div className="prediction-actions">
          <div className="prediction-actions__section">
            <h3 className="prediction-actions__title">
              Prediction Type
            </h3>
            <div className="prediction-actions__buttons">
              <button
                onClick={handleSinglePrediction}
                disabled={loading || !selectedModelId}
                className="btn btn--primary btn--full"
              >
                {loading ? 'Processing...' : 'Single Prediction'}
              </button>
              
              <button
                onClick={handleBatchPrediction}
                disabled={loading || !selectedModelId || !uploadedFile}
                className="btn btn--success btn--full"
              >
                {loading ? 'Processing...' : 'Batch Prediction'}
              </button>
            </div>
          </div>

          {success && (
            <div className="alert alert--success">
              Prediction completed successfully!
            </div>
          )}

          {batchResultInfo && (
            <div className="batch-result-info">
              <div className="batch-result-info__header">
                <h4 className="batch-result-info__title">Batch Prediction Results</h4>
                <button onClick={clearResults} className="results-section__clear-btn">
                  Clear
                </button>
              </div>
              <div className="batch-result-info__details">
                <div className="batch-result-info__item">
                  <strong>Total Predictions:</strong> {batchResultInfo.totalPredictions}
                </div>
                <div className="batch-result-info__item">
                  <strong>Results File:</strong> {batchResultInfo.resultsFilename}
                </div>
                <div className="batch-result-info__item">
                  <strong>Model:</strong> {batchResultInfo.modelType} ({batchResultInfo.datasetName})
                </div>
                {batchResultInfo.predictionSummary && (
                  <div className="batch-result-info__summary">
                    <strong>Prediction Summary:</strong>
                    {batchClassBreakdown ? (
                      <ul>
                        {batchClassBreakdown.map((entry) => (
                          <li key={entry.label}>
                            {entry.label}: {entry.count} ({entry.percent.toFixed(1)}%)
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul>
                        <li>Mean: {batchResultInfo.predictionSummary.mean_prediction?.toFixed(2)}</li>
                        <li>Std: {batchResultInfo.predictionSummary.std_prediction?.toFixed(2)}</li>
                        <li>Min: {batchResultInfo.predictionSummary.min_prediction?.toFixed(2)}</li>
                        <li>Max: {batchResultInfo.predictionSummary.max_prediction?.toFixed(2)}</li>
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <ErrorState
              message={error}
              onRetry={() => {
                setError(null);
                if (uploadedFile) {
                  handleBatchPrediction();
                } else {
                  handleSinglePrediction();
                }
              }}
            />
          )}
        </div>
      </div>


    </div>
  );
};

export default ModelPrediction;